/**
 * Utilitaires de formatage de texte pour l'interface utilisateur.
 */

/**
 * Formatage du prénom : 
 * Met la première lettre de chaque mot (séparé par espace, tiret, ou apostrophe) en majuscule.
 * Le reste en minuscule.
 */
export const formatFirstName = (value: string): string => {
    if (!value) return value
    // On passe tout en minuscule d'abord pour le "reste en minuscule"
    const lower = value.toLowerCase()
    return lower.replace(/(^|[- \s'])([a-zà-öø-ÿ])/gi, (_, sep, char) => sep + char.toUpperCase())
}

/**
 * Formatage du nom de famille :
 * Tout en majuscules.
 */
export const formatLastName = (value: string): string => {
    if (!value) return value
    return value.toUpperCase()
}

/**
 * Formatage d'un contact composite (titre + prénom + nom) destiné à
 * l'affichage en colonne DataTable, en-tête de carte ou destinataire d'email.
 *
 * Règles :
 *   - Civilité courte (ex: "M.", "Mme") préfixée si présente, sinon ignorée.
 *   - Prénom INITCAP, nom UPPER (cohérent avec l'annuaire DIR-001).
 *   - Si tout est vide / null → chaîne vide (caller décide du fallback).
 *
 * Utilisé sur les 3 fiches logistiques (lieux, restauration, hébergement)
 * pour exposer un libellé unique à partir des 3 champs séparés. Mutualisé
 * pour éviter la dérive d'affichage entre listings et détails.
 */
export const formatContactFullName = (
    title: { short_form: string | null } | null | undefined,
    firstName: string | null | undefined,
    lastName: string | null | undefined,
): string => {
    const parts: string[] = []
    if (title?.short_form) parts.push(title.short_form)
    if (firstName) parts.push(formatFirstName(firstName))
    if (lastName) parts.push(formatLastName(lastName))
    return parts.join(' ')
}


/**
 * Abréviations courantes des voies françaises rencontrées dans les bases
 * INSEE / Annuaire Santé / RNB, à transformer en libellé long pour
 * l'affichage. Les clés sont en minuscules pour le match (la valeur de
 * sortie est en Title Case typique français).
 */
const STREET_TYPE_ABBREVIATIONS: Record<string, string> = {
    'r': 'Rue',
    'av': 'Avenue',
    'ave': 'Avenue',
    'bd': 'Boulevard',
    'blvd': 'Boulevard',
    'pl': 'Place',
    'imp': 'Impasse',
    'all': 'Allée',
    'all.': 'Allée',
    'che': 'Chemin',
    'chem': 'Chemin',
    'rte': 'Route',
    'rt': 'Route',
    'crs': 'Cours',
    'sq': 'Square',
    'qu': 'Quai',
    'pas': 'Passage',
    'lieu-dit': 'Lieu-dit',
    'ld': 'Lieu-dit',
    'sente': 'Sente',
    'voie': 'Voie',
    'vlge': 'Village',
    'res': 'Résidence',
    'rés': 'Résidence',
    'zac': 'ZAC',
    'zi': 'ZI',
    'cs': 'CS',
    'bp': 'BP',
}


/**
 * Particules qui restent en minuscules dans une adresse / un nom propre
 * (sauf en tête de chaîne où elles sont capitalisées).
 */
const FRENCH_PARTICLES = new Set([
    'de',
    'du',
    'des',
    'la',
    'le',
    'les',
    'en',
    'sur',
    'sous',
    'aux',
    'au',
    'et',
    'à',
    'd',
    'l',
])


/**
 * Formatage d'une ligne d'adresse en Title Case français lisible.
 *
 * Source typique : Annuaire Santé ANS / INSEE qui retournent tout en
 * MAJUSCULES sans accents (ex: `"1 R CHARLES D ORLEANS"` → on souhaite
 * afficher `"1 Rue Charles D'Orléans"` aux nuances accents près).
 *
 * Règles :
 *   1. Numéros (chiffres + suffixes BIS/TER/QUATER) gardés tels quels.
 *   2. Abréviations courantes (R → Rue, AV → Avenue, BD → Boulevard, etc.)
 *      remplacées par le libellé long.
 *   3. Particules ("de", "du", "la", "le", "des", "en", "sur"…) en minuscules
 *      sauf en début de chaîne.
 *   4. "D" / "L" isolés en début de mot : transformés en "D'" / "L'" pour
 *      restaurer les apostrophes mangées par l'export INSEE.
 *   5. Reste : Title Case (première lettre de chaque mot en majuscule).
 *
 * Les accents NE sont PAS restaurés (impossible sans dictionnaire toponymique
 * complet). On accepte "Charles D'Orleans" plutôt que "Charles D'Orléans".
 */
export const formatStreetAddress = (value: string | null | undefined): string => {
    if (!value) return ''
    const cleaned = value.trim()
    if (!cleaned) return ''

    const tokens = cleaned.split(/\s+/)
    const out: string[] = []

    for (let i = 0; i < tokens.length; i++) {
        const raw = tokens[i]!
        const lower = raw.toLowerCase()

        // 1. Pur numéro ou code (chiffres uniquement, ou chiffres+lettres
        // type "12B", "BIS", "TER") → conserver en majuscules pour la
        // lisibilité ("12 BIS Rue X" reste plus lisible que "12 bis...").
        if (/^\d+[A-Z]*$/i.test(raw)) {
            out.push(raw.toUpperCase())
            continue
        }
        if (/^(BIS|TER|QUATER)$/i.test(raw)) {
            out.push(raw.toUpperCase())
            continue
        }

        // 2. Abréviation de type de voie (en début uniquement — éviter de
        // transformer "Rd" qui est aussi un nom propre).
        if (i <= 1 && lower in STREET_TYPE_ABBREVIATIONS) {
            out.push(STREET_TYPE_ABBREVIATIONS[lower]!)
            continue
        }

        // 3. Particule en minuscules, sauf en début de chaîne.
        if (i > 0 && FRENCH_PARTICLES.has(lower)) {
            out.push(lower)
            continue
        }

        // 4. "D" / "L" isolés (anciens "D'", "L'" sans apostrophe) → restaure.
        if (lower === 'd' || lower === 'l') {
            out.push(lower.toUpperCase() + "'")
            continue
        }

        // 5. Title Case standard (gère composés à tirets / apostrophes).
        out.push(
            lower.replace(/(^|[- '])([a-zà-öø-ÿ])/g, (_, sep, ch) => sep + ch.toUpperCase()),
        )
    }

    // Recolle "D'" / "L'" au mot suivant : ["D'", "Orleans"] → "D'Orleans"
    const merged: string[] = []
    let buffer = ''
    for (const tok of out) {
        if (buffer) {
            merged.push(buffer + tok)
            buffer = ''
        } else if (tok.endsWith("'") && (tok === "D'" || tok === "L'")) {
            buffer = tok
        } else {
            merged.push(tok)
        }
    }
    if (buffer) merged.push(buffer)

    return merged.join(' ')
}


/**
 * Formatage d'un nom de ville en Title Case français lisible.
 *
 * Sources typiques : INSEE / Annuaire Santé qui retournent en majuscules
 * ("COGNAC", "DIJON CEDEX", "SAINT-NAZAIRE"). On veut afficher en Title
 * Case avec gestion des particules ("Saint-Nazaire", "Aix-en-Provence",
 * "Le Havre", "L'Île-Rousse").
 */
export const formatCityName = (value: string | null | undefined): string => {
    if (!value) return ''
    const cleaned = value.trim()
    if (!cleaned) return ''
    // On retire les suffixes administratifs courants ("CEDEX", "CEDEX 9")
    // qui surchargent l'affichage sans valeur ajoutée.
    const withoutCedex = cleaned.replace(/\s+CEDEX(\s+\d+)?$/i, '')
    // Title Case avec gestion des particules + apostrophes + tirets composés.
    return withoutCedex
        .toLowerCase()
        .split(/\s+/)
        .map((word, idx) => {
            // Particule en minuscules sauf en tête.
            if (idx > 0 && FRENCH_PARTICLES.has(word)) return word
            // Composés : "saint-nazaire" → "Saint-Nazaire".
            return word.replace(
                /(^|[- '])([a-zà-öø-ÿ])/g,
                (_, sep, ch) => sep + ch.toUpperCase(),
            )
        })
        .join(' ')
}

/**
 * Téléphone lisible et homogène, quelle que soit la façon dont il a été saisi
 * ou lu (un CV écrit indifféremment « 06.70.12.37.64 », « 0670123764 »,
 * « +33 6 70 12 37 64 »).
 *
 * Règle : on ne garde que les chiffres (et un « + » de tête), puis on groupe
 * par 2 — le format téléphonique français usuel. Un numéro international est
 * conservé tel quel, indicatif détaché : « +33 6 70 12 37 64 ».
 * Une saisie non reconnue est renvoyée inchangée (jamais de perte de donnée).
 */
export const formatPhoneNumber = (value: string | null | undefined): string => {
    if (!value) return ''
    const trimmed = value.trim()
    if (!trimmed) return ''
    // Saisie contenant du texte (« poste 4212 », « n/a ») : ce n'est pas un
    // numéro à normaliser — on la respecte telle quelle plutôt que de la mutiler.
    if (/[a-zà-öø-ÿ]/i.test(trimmed)) return trimmed
    const hasPlus = trimmed.startsWith('+')
    const digits = trimmed.replace(/\D/g, '')
    if (!digits) return trimmed

    /** Groupes de 2 ; un reliquat impair est isolé EN TÊTE (« 6 70 12 37 64 »,
     *  le 6 étant le premier chiffre du numéro national sans son 0). */
    const groupByTwo = (input: string): string => {
        const head = input.length % 2 === 1 ? input.slice(0, 1) : ''
        const pairs = input.slice(head.length).match(/\d{2}/g) ?? []
        return [head, ...pairs].filter(Boolean).join(' ')
    }

    if (hasPlus) {
        // Indicatif pays détaché (2 chiffres, 3 au-delà de 11 — ex. +262).
        const cc = digits.slice(0, digits.length > 11 ? 3 : 2)
        const rest = groupByTwo(digits.slice(cc.length))
        return `+${cc}${rest ? ` ${rest}` : ''}`
    }
    // National français : 10 chiffres → 5 paires.
    return groupByTwo(digits)
}
